/**
 * Feed Alerts Admin API Service
 * Handles API calls for feed issue alert management.
 */

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || error.error || 'Request failed');
  }

  return response.json();
};

export interface FeedAlert {
  id: string;
  customer_name: string;
  customer_phone: string;
  contact_person: string;
  flock_name: string | null;
  source: string;
  detected_text: string;
  matched_keywords: string[];
  issue_category: string;
  likely_cause: string;
  suggested_action: string;
  priority: 'high' | 'medium' | 'low';
  status: 'new' | 'viewed' | 'contacted' | 'resolved' | 'dismissed';
  assigned_to: string | null;
  resolution_notes: string;
  resolved_at: string | null;
  email_sent: boolean;
  created_at: string;
}

export interface FeedAlertCounts {
  new: number;
  high_priority: number;
  total_unresolved: number;
}

export interface FeedAlertFilters {
  status?: string;
  priority?: string;
  category?: string;
  show_all?: string;
}

export interface FeedAlertUpdate {
  status?: string;
  resolution_notes?: string;
  assigned_to?: number;
}

const feedAlertsAdminAPI = {
  getAlerts: (params?: FeedAlertFilters): Promise<FeedAlert[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.priority) searchParams.set('priority', params.priority);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.show_all) searchParams.set('show_all', params.show_all);
    const qs = searchParams.toString();
    return apiCall(`/admin/feed-alerts/${qs ? `?${qs}` : ''}`);
  },

  updateAlert: (id: string, data: FeedAlertUpdate): Promise<{ id: string; status: string; resolution_notes: string; resolved_at: string | null }> => {
    return apiCall(`/admin/feed-alerts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getCounts: (): Promise<FeedAlertCounts> => {
    return apiCall('/admin/feed-alerts/counts/');
  },
};

export default feedAlertsAdminAPI;
